import React from 'react';
import { X, Image as ImageIcon, BookOpen } from 'lucide-react';

interface PasiGuideModalProps {
  onClose: () => void;
}

export default function PasiGuideModal({ onClose }: PasiGuideModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">PASI User Guide & Atlas</h2>
              <p className="text-sm text-slate-500">Psoriasis Area and Severity Index</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          
          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-sm">1</span>
              Assess the severity of each of the three signs
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Grade the severity of each sign (Erythema/Redness, Induration/Thickness, Desquamation/Scaling) on a scale of 0 to 4:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { grade: 0, label: 'None' },
                { grade: 1, label: 'Mild' },
                { grade: 2, label: 'Moderate' },
                { grade: 3, label: 'Severe' },
                { grade: 4, label: 'Very Severe' },
              ].map(s => (
                <div key={s.grade} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-slate-800 mb-1">{s.grade}</div>
                  <div className="text-sm font-semibold text-slate-600">{s.label}</div>
                </div>
              ))}
            </div>
          </section>

          <hr className="border-slate-100" />

          <section className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-sm">2</span>
              Assess the extent of Area Involvement
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Grade the area of involvement for each body region on a scale of 0 to 6:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-center">
              {[
                { grade: 0, label: '0%' },
                { grade: 1, label: '< 10%' },
                { grade: 2, label: '10 - 29%' },
                { grade: 3, label: '30 - 49%' },
                { grade: 4, label: '50 - 69%' },
                { grade: 5, label: '70 - 89%' },
                { grade: 6, label: '90 - 100%' },
              ].map(s => (
                <div key={s.grade} className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                  <div className="text-lg font-bold text-slate-800">{s.grade}</div>
                  <div className="text-xs font-semibold text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}